/*
  Acne-type quiz — results page controller.

  Reads the answers the quiz left in sessionStorage, asks
  assets/norse-quiz-acne-types.js what they mean, and fills the page in. All of the
  diagnosis is decided there; this file only puts strings and attributes on nodes.

  Deliberately not a custom element: it has to run once over markup several sections
  produced, and those sections are independent of each other in the template.
*/
(function () {
  'use strict';

  /*
    Four instances of the section emit this tag, so the browser executes the file four
    times even though it fetches it once. Without the guard the add-on button collects
    four click listeners and one tap toggles it four times — a net no-op that reads as
    a dead button. The tags are deliberately not de-duplicated in Liquid: any one of
    the four parts may be the only one a merchant keeps.
  */
  if (window.__norseAcneResults) return;
  window.__norseAcneResults = true;

  // Same key the quiz writes — see STORAGE_KEY in assets/norse-quiz-state.js.
  var STORAGE_KEY = 'norse-quiz';
  var ROOT_TIER = 'data-atq-tier';
  var ROOT_FORMULA = 'data-atq-formula';

  function engine() {
    return window.NorseAcneTypes || null;
  }

  /*
    One flat object for whichever quiz ran last — `{answers, labels, tokens, completed,
    quizId}` (see `_persist` in assets/norse-quiz.js), NOT a map keyed by quiz. So the
    quiz id has to be checked: four quizzes share this key, and rendering an acne
    diagnosis out of the skin-concern quiz's answers would be worse than showing none.

    Anything unreadable counts as no answers, which lands on the fallback rather than
    throwing on a page the shopper has already paid attention to.
  */
  function readState(quizId) {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (parsed.quizId && parsed.quizId !== quizId) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  }

  /** The email step writes it as a token; a skipped gate simply leaves it unset. */
  function nameFrom(state) {
    return (state && state.tokens && state.tokens.first_name) || '';
  }

  function each(selector, root, fn) {
    Array.prototype.forEach.call((root || document).querySelectorAll(selector), fn);
  }

  /** Conditional sections leave no gap: the whole section goes, not just its contents. */
  function drop(node) {
    if (node && node.parentNode) node.parentNode.removeChild(node);
  }

  function text(node, value) {
    if (node) node.textContent = value;
  }

  /*
    A skipped email gate means no name, and the headline then has to read as a sentence
    on its own — "You're showing…" rather than ", you're showing…". Capitalising the
    next word is the whole of the fallback.
  */
  function capitalise(value) {
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
  }

  function buildHeadline(answers, shopperName, E) {
    var voice = E.subject(answers).are;
    var types = E.typesSentence(answers);
    var lead = shopperName ? shopperName + ', ' + voice : capitalise(voice);
    return { lead: lead, types: types };
  }

  function fillDiagnosis(section, answers, shopperName, E) {
    var shown = E.shownTypes(answers);
    var headline = buildHeadline(answers, shopperName, E);

    text(section.querySelector('[data-atq-headline-lead]'), headline.lead + ' showing ');
    text(section.querySelector('[data-atq-headline-types]'), headline.types);

    // The pill repeats Q3 in the shopper's own words; with Q3 unanswered it has nothing
    // to say and is removed rather than left blank.
    var pill = section.querySelector('[data-atq-severity-pill]');
    var severity = answers.severity && answers.severity[0];
    if (pill) {
      if (severity && pill.dataset[severity]) text(pill, pill.dataset[severity]);
      else drop(pill);
    }

    var list = section.querySelector('[data-atq-cards]');
    var template = section.querySelector('[data-atq-card-template]');
    if (!list || !template) return;

    /*
      The template STAYS in the DOM, hidden, and the cards it produced are cleared
      first. This used to remove the template once it had been cloned, which was
      fine while the page filled exactly once — but a shopper who reopens the quiz
      with ?quiz=1 and answers differently finishes on this same document, and the
      second pass then found no template, returned early, and left the first run's
      diagnosis on screen under the new answers.
    */
    each('[data-atq-card]', list, drop);

    shown.forEach(function (type, index) {
      var card = template.cloneNode(true);
      card.removeAttribute('data-atq-card-template');
      card.setAttribute('data-atq-card', '');
      card.hidden = false;
      if (index === 0) card.classList.add('atq__card--lead');
      text(card.querySelector('[data-atq-card-name]'), E.TYPE_NAMES[type]);
      text(card.querySelector('[data-atq-card-rank]'), E.RANK_LABELS[index] || '');
      text(card.querySelector('[data-atq-card-why]'), E.TYPE_WHY[type]);
      list.appendChild(card);
    });
  }

  /*
    The tiles are lit by Q7 and by the types, not by Q7 alone: someone who never ticked
    "hormonal imbalance" but whose answers produced hormonal acne still has the hormone
    tile lit, because the section's claim is about what is driving THEIR acne.
  */
  function fillRootCause(section, answers, E) {
    var types = E.computeTypes(answers);
    var shown = E.shownTypes(answers);
    var concerns = answers.concerns || [];
    var hasIn = function (list, value) {
      return list.indexOf(value) > -1;
    };

    var lit = {
      hormones: hasIn(concerns, 'hormonal') || hasIn(types, 'hormonal'),
      gut: hasIn(concerns, 'gut'),
      liver: hasIn(concerns, 'gut'),
      inflammation:
        hasIn(concerns, 'redness') || hasIn(types, 'inflammatory') || hasIn(types, 'cystic'),
    };

    /*
      Only the counted half is generated. Figma 9790:948 sets the second sentence in a
      heavier Migra than the first, so it is its own element in the markup with its own
      setting — writing the whole heading through `textContent` here, as this did, made
      the two weights impossible.
    */
    var count = shown.length;
    var word = E.COUNT_WORDS[count] || String(count);
    var heading = section.querySelector('[data-atq-cause-heading]');
    if (heading) {
      text(heading, count === 1 ? 'One type.' : word + ' types.');
    }

    each('[data-atq-tile]', section, function (tile) {
      tile.setAttribute('data-atq-on', lit[tile.dataset.atqTile] ? 'true' : 'false');
    });
  }

  function fillRitual(section, answers, E) {
    var list = section.querySelector('[data-atq-reasons]');
    var template = section.querySelector('[data-atq-reason-template]');
    if (!list || !template) return;

    // Cleared and refilled rather than appended to, so a retake replaces the rows
    // instead of stacking a second set under the first. Same reasoning as the type
    // cards above; the template stays put.
    each('[data-atq-reason]', list, drop);

    var rows = E.reasonRows(answers);
    list.hidden = !rows.length;
    if (!rows.length) return;

    rows.forEach(function (row) {
      var item = template.cloneNode(true);
      item.removeAttribute('data-atq-reason-template');
      item.setAttribute('data-atq-reason', '');
      item.hidden = false;
      text(item, row);
      list.appendChild(item);
    });
  }

  /*
    Option 3 only. It used to be tier 1 only, back when the tier picked the product;
    the CRO's design makes the OFFER that choice — options 1 and 2 already bundle a
    ritual, and only the supplements-only box has a surface layer to add. The offer
    is published from Liquid before this runs, so reading it here is safe.

    NOTE: the click still only records intent (`data-atq-addon-selected` + the
    `norse:acne-addon` event). Nothing consumes it now that the buy box is a
    `bundles-free-gift` section owning its own cart payload — see the buy box's
    `_buildItems`, where an extra paid line would change the parent, the bundle size
    the guard checks and the per-item selling plans. That wiring is still to do.
  */
  function fillAddon(section, answers, E) {
    if (document.documentElement.getAttribute('data-atq-offer') !== '3') {
      drop(section);
      return;
    }
    var box = section.querySelector('[data-atq-addon]');
    if (!box) return;

    // One listener, however many times this runs. Two would make a single tap add
    // the line twice — see the same guard at the top of this file for why four
    // instances of the section emitting one script tag needed the identical fix.
    if (box.getAttribute('data-atq-addon-bound') === 'true') return;
    box.setAttribute('data-atq-addon-bound', 'true');

    /*
      "Add to order" adds ONE line: this variant, on its own selling plan, and
      nothing else. It is NOT part of the bundle — no `_lp_*` properties — so
      assets/bfg-bundle-guard.js neither counts it towards the bundle size nor
      sweeps it when the bundle changes. The buy box keeps owning its own payload.

      One-way on purpose: the button reports that it added and stops there, rather
      than turning into "Remove". Undoing belongs in the cart, where the shopper can
      see the line they would be removing; a second click here silently mutating a
      cart they are not looking at is the worse affordance.
    */
    box.addEventListener('click', function (event) {
      var button = event.target.closest('[data-atq-addon-btn]');
      if (!button) return;
      if (box.getAttribute('data-atq-added') === 'true' || button.disabled) return;

      var variantId = (box.dataset.variantId || '').trim();
      if (!variantId) return;

      var body = {
        items: [{ id: Number(variantId), quantity: 1 }],
      };
      var plan = (box.dataset.sellingPlanId || '').trim();
      if (plan) body.items[0].selling_plan = Number(plan);

      var cart =
        document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map(function (s) {
          return s.id;
        });
        body.sections_url = window.location.pathname;
      }

      button.disabled = true;
      var original = button.textContent;

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          // A Cart API rejection comes back 4xx with `status` set, not as a throw.
          if (data && data.status) throw new Error(data.description || data.message || 'add failed');

          box.setAttribute('data-atq-added', 'true');
          text(button, box.dataset.addedLabel || original);
          document.documentElement.setAttribute('data-atq-addon-selected', 'true');
          document.dispatchEvent(new CustomEvent('norse:acne-addon', { detail: { added: true } }));

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'atq-addon',
              productVariantId: variantId,
              cartData: data,
            });
          }
          if (cart && typeof cart.renderContents === 'function') cart.renderContents(data);
        })
        .catch(function (error) {
          // Re-enable rather than leaving a dead button: the shopper can retry.
          button.disabled = false;
          text(button, original);
          box.setAttribute('data-atq-added', 'false');
          console.error('atq-addon: add to cart failed', error);
        });
    });
  }

  /*
    What is inside grows with the tier: the two supplements always, the ritual from
    tier 2, the scars balm at tier 3. The formula only ever renames the second row.
  */
  function fillInside(section, answers, E) {
    var tier = E.resolvedTier(answers);
    var formula = E.formula(answers);
    var list = section.querySelector('[data-atq-inside]');
    if (!list) return;

    each('[data-atq-inside-row]', list, function (row) {
      var minTier = parseInt(row.dataset.atqMinTier || '1', 10);
      var onlyTier = row.dataset.atqOnlyTier ? parseInt(row.dataset.atqOnlyTier, 10) : null;
      var keep = tier >= minTier && (onlyTier === null || tier === onlyTier);
      /*
        Hidden rather than removed, which is visually identical — `hidden` is
        `display: none` and the list is a flow container, so a hidden row leaves no
        gap — but reversible. A retake that resolves a HIGHER tier has to be able to
        bring the ritual and the scars balm back, and a removed row never returns.
      */
      row.hidden = !keep;
      if (!keep) return;
      each('[data-atq-formula-token]', row, function (token) {
        text(token, formula);
      });
    });
  }

  /*
    Nothing resolved: no answers, or an incomplete run reached by URL. The page still
    has to sell something, so the section that declares itself the fallback is shown
    and the personalised copy is dropped rather than rendered half-filled.
  */
  function showFallback(E, answers) {
    var root = document.documentElement;
    var fallback = document.querySelector('[data-atq-fallback]');
    var tier = fallback ? fallback.getAttribute('data-atq-fallback') : '1';
    root.setAttribute(ROOT_TIER, tier);
    var formula = resolveFormula(E, answers);
    root.setAttribute(ROOT_FORMULA, formula);
    publishGender(formula);
  }

  /*
    `?formula=male|female` (or `?gender=`) forces the formula, so the male and female
    results pages can be compared without retaking the quiz as the other gender. Sticky
    for the session and cleared with `off`, exactly like `?offer=` — see the inline
    script in sections/norse-acne-results.liquid, which is also what publishes the
    `data-atq-test-params` flag that enables this. Off, the answers alone decide.

    Only `male` and `female` are honoured: anything else falls through to the answers
    rather than being written, because an unknown value would pick no product at all.
  */
  var FORMULA_KEY = 'atq-formula-override';

  function formulaOverride() {
    if (!document.documentElement.hasAttribute('data-atq-test-params')) return null;
    try {
      var q = new URLSearchParams(window.location.search);
      var asked = q.get('formula') || q.get('gender');
      if (asked === 'off') window.sessionStorage.removeItem(FORMULA_KEY);
      else if (asked === 'male' || asked === 'female') window.sessionStorage.setItem(FORMULA_KEY, asked);
      var held = window.sessionStorage.getItem(FORMULA_KEY);
      return held === 'male' || held === 'female' ? held : null;
    } catch (e) {
      return null; // private mode blocks sessionStorage
    }
  }

  /**
   * The formula the page should render: the override if there is one, else the answers.
   *
   * Asks `AtqOffer` first so the attribute this writes can never disagree with the one
   * the inline script already put on <html> at parse time — a disagreement here is a
   * visible gallery swap after paint. The local path below still stands for a template
   * that places some other part of the section without the diagnosis.
   */
  function resolveFormula(E, answers) {
    if (window.AtqOffer && typeof window.AtqOffer.formula === 'function') {
      var resolved = window.AtqOffer.formula(answers || null);
      if (resolved) return resolved;
    }
    return formulaOverride() || (answers && E ? E.formula(answers) : 'female');
  }

  /*
    The buy boxes are `bundles-free-gift` sections, which pick their product and
    gallery from `<html data-norse-gender>` rather than from the quiz. The CRO's
    frames draw no FEMALE/MALE selector — Q2 already asked — so the answer has to
    drive that state directly or every male shopper is sold the female formula.
    Guarded because norse-gender-switch.js only loads when a page renders a switch.
  */
  function publishGender(formula) {
    if (window.NorseGender && typeof window.NorseGender.set === 'function') {
      window.NorseGender.set(formula, { force: true });
    } else {
      document.documentElement.setAttribute('data-norse-gender', formula);
    }
  }

  /* ---------------------------------------------------------------- reviews */

  /*
    Clicking the buy box's star rating scrolls to this page's review carousel.

    The rating is a shared `bundles-free-gift` block and its link is
    `<a href="#looxReviews">`. On a product page that works, but NOT because the id is
    an anchor: `#looxReviews` is the **Loox app block's container**, and Loox renders
    its review widget into whatever element carries it. So the obvious fix — give the
    carousel that id — does not redirect a link, it hands the section to Loox, which
    replaces the ten quiz reviews with the PDP widget. That was tried on 2026-09-16 and
    is what QA saw. Never put that id on a section of ours.

    The destination is therefore resolved here, by the section's own schema class, and
    the anchor is never followed. Scoped to this page because `start()` only runs where
    `[data-atq-page]` exists, and to the quiz's own buy boxes by `.atq-offerbox`.
  */
  var REVIEWS_SECTION = '.norse-quiz-before-after-section';
  var reviewScrollBound = false;

  /** How much of the viewport the results header is covering, or 0 if it is not stuck. */
  function stickyHeaderHeight() {
    var header = document.querySelector('.norse-quiz-results-header-section');
    if (!header) return 0;
    var position = window.getComputedStyle(header).position;
    if (position !== 'sticky' && position !== 'fixed') return 0;
    return header.getBoundingClientRect().height;
  }

  function reviewsSection() {
    // The carousel when there is one, but any instance rather than none: a template
    // that switches the section back to the grid still has the reviews in it.
    var carousel = document.querySelector(REVIEWS_SECTION + ' .nqba--carousel');
    return (carousel && carousel.closest(REVIEWS_SECTION)) || document.querySelector(REVIEWS_SECTION);
  }

  function bindReviewScroll() {
    if (reviewScrollBound) return;
    reviewScrollBound = true;

    document.addEventListener('click', function (event) {
      var node = event.target;
      if (!node || !node.closest) return;
      var link = node.closest('.atq-offerbox a.rating-wrapper');
      if (!link) return;

      var section = reviewsSection();
      // Nothing to scroll to: leave the click alone rather than swallow it. The
      // href at least still means something on a page that does carry a widget.
      if (!section) return;

      event.preventDefault();

      var reduced =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      /*
        `scrollIntoView` would put the carousel's heading under the results header,
        which is sticky. Measured rather than hard-coded, and only counted when the
        bar really is stuck — a template that places this section without a sticky
        header must not get a phantom offset.
      */
      var top = section.getBoundingClientRect().top + window.pageYOffset - stickyHeaderHeight();
      window.scrollTo({ top: Math.max(0, top), behavior: reduced ? 'auto' : 'smooth' });

      /*
        Move focus with the viewport, or a keyboard user activates the link and their
        focus stays on a control now a screen and a half above them. `preventScroll`
        because focusing would otherwise jump instantly and undo the smooth scroll.
      */
      if (!section.hasAttribute('tabindex')) section.setAttribute('tabindex', '-1');
      section.focus({ preventScroll: true });
    });
  }

  /* ------------------------------------------------------------------- cart */

  /*
    What has to ride along with the supplements. Tier 2 and 3 always carry theirs;
    tier 1 carries the add-on only when the shopper has taken it, which lands the
    order on exactly tier 2's basket.
  */
  function companionForCart(answers, E) {
    var included = E.companion(answers);
    if (included) return included;
    if (
      E.resolvedTier(answers) === 1 &&
      document.documentElement.getAttribute('data-atq-addon-selected') === 'true'
    ) {
      return E.addon(answers);
    }
    return null;
  }

  /*
    ONE `/cart/add.js` carrying both lines, not two calls in sequence.

    Shopify treats a multi-item add as all-or-nothing, and that is the property worth
    having here: the failure mode of two sequential adds is a shopper who paid for the
    supplements and never received the ritual the page told them they were buying. If
    anything in this path throws, the listener stands down and the theme's own handler
    submits the supplements alone — the shopper can still buy, and that is a better
    failure than a dead button.

    The selling plan is read off the form rather than pinned, so the subscribe /
    one-time toggle carries to the companion: a one-time order must not smuggle a
    subscription onto its second line.
  */
  var cartBound = false;
  var latestAnswers = null;

  function bindCart(answers, E) {
    /*
      Document-level and capture-phase, so binding it twice would send the basket
      twice — the second call would also `preventDefault` an event the first has
      already handled. `apply()` can run again after a retake, so the guard is here
      rather than at the call site.

      The answers are read through the flag rather than captured: a retake replaces
      them, and a listener closed over the first run's object would keep adding the
      companion the shopper has just answered their way out of.
    */
    if (cartBound) {
      latestAnswers = answers;
      return;
    }
    cartBound = true;
    latestAnswers = answers;

    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || !form.closest || !form.closest('.atq-buybox')) return;

        companion = latestAnswers ? companionForCart(latestAnswers, E) : null;
        if (!companion) return;

        var productForm = form.closest('product-form');
        var cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        var button = productForm && productForm.querySelector('[type="submit"]');
        if (!productForm || !cart || !cart.getSectionsToRender) return;

        // Ahead of the theme's own listener — this is registered on `document` in the
        // capture phase, because a listener on the form itself would run second.
        event.preventDefault();
        event.stopImmediatePropagation();

        var data = new FormData(form);
        var variantId = data.get('id');
        var sellingPlan = data.get('selling_plan');
        if (!variantId) return;

        var items = [{ id: Number(variantId), quantity: 1 }];
        var line = { id: companion.variant, quantity: 1 };
        // A blank hidden input reads as '' — only a real plan id may be sent on.
        if (sellingPlan) {
          items[0].selling_plan = Number(sellingPlan);
          line.selling_plan = companion.plan;
        }
        items.push(line);

        var spinner = productForm.querySelector('.loading__spinner');
        if (button) {
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('loading');
        }
        if (spinner) spinner.classList.remove('hidden');

        function done() {
          if (button) {
            button.removeAttribute('aria-disabled');
            button.classList.remove('loading');
          }
          if (spinner) spinner.classList.add('hidden');
        }

        window
          .fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: items,
              sections: cart.getSectionsToRender().map(function (section) {
                return section.id;
              }),
              sections_url: window.location.pathname,
            }),
          })
          .then(function (response) {
            return response.json();
          })
          .then(function (response) {
            done();
            if (response.status) {
              // Nothing was added — Shopify rejected the whole basket.
              if (window.console && console.warn) console.warn('[atq] cart add failed', response);
              document.dispatchEvent(
                new CustomEvent('norse:acne-cart-error', { detail: response })
              );
              return;
            }
            if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
              window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                source: 'norse-acne-results',
                productVariantId: variantId,
                cartData: response,
              });
            }
            cart.renderContents(response);
          })
          .catch(function (error) {
            done();
            if (window.console && console.warn) console.warn('[atq] cart add failed', error);
            document.dispatchEvent(new CustomEvent('norse:acne-cart-error', { detail: error }));
          });
      },
      true
    );
  }

  /*
    Re-publish the offer now that the answers are known.

    The buy box a shopper sees is decided by their tier, and the resolving lives in
    `AtqOffer` (the inline script in sections/norse-acne-results.liquid), which runs
    at parse time so the first paint is already right. At parse time, though, a
    first-time visitor has no answers — the quiz overlay is still covering this page
    — so it lands on the section's fallback. Calling it again here is what makes the
    offer follow the answers on the run that produced them.

    Guarded because the diagnosis instance is what defines it, and a template could
    place any other part of this section on its own.
  */
  function publishOffer(answers) {
    if (window.AtqOffer && typeof window.AtqOffer.publish === 'function') {
      window.AtqOffer.publish(answers || null);
    }
  }

  /*
    The state the QUIZ reads, unfiltered by quiz id.

    `readState` filters, and it must: rendering an acne diagnosis out of another
    quiz's answers would be worse than showing none. But the decision below is
    "is the overlay on this page about to run", and the overlay reads the key
    exactly like this — see `_decideInitialVisibility` in assets/norse-quiz.js.
    Filtering here would make the two disagree and strand the page waiting for a
    quiz that already decided not to open.
  */
  function rawState() {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  }

  /*
    Is the quiz overlay going to run before the shopper sees this page?

    <norse-quiz> is a full-viewport overlay sharing this URL, NOT a separate page, so
    finishing it is not a navigation and nothing here re-runs on its own. That is the
    whole of the "no content on top of the results page until I refresh" bug: this
    file ran at DOMContentLoaded, found no answers because the shopper had not
    answered anything yet, took the fallback path and REMOVED every personalised
    section — the diagnosis included — while the overlay hid the damage. It also
    published the female formula, which is the pink gallery under a male answer.

    So when the overlay is going to run, nothing is filled and nothing is dropped
    until `norse:quiz-complete`. The three cases below mirror the element's own
    decision exactly; disagreeing either strands the page blank or drops the
    diagnosis a moment before the answers arrive.
  */
  function quizWillRun() {
    if (!document.querySelector('norse-quiz')) return false;
    try {
      var forced = new URLSearchParams(window.location.search).get('quiz');
      if (forced === '0') return false; // results forced, overlay never opens
      if (forced === '1') return true; // retake forced, overlay always opens
    } catch (error) {
      /* fall through to the stored state */
    }
    var saved = rawState();
    return !(saved && saved.completed);
  }

  /*
    Everything the answers decide, in one pass.

    Safe to call more than once: it runs on load for a shopper who already has
    answers, and again on `norse:quiz-complete` for one who has just produced them.
    Every fill below either overwrites or clears what it wrote last time — see the
    note on the card list in `fillDiagnosis`.
  */
  function apply() {
    var page = document.querySelector('[data-atq-page]');
    if (!page) return;

    var E = engine();
    var quizId = page.getAttribute('data-atq-page') || 'acne-type-quiz';
    var state = readState(quizId);
    var answers = state && state.answers;
    var answered = !!(E && E.hasAnyAnswer(answers));

    // Published first, so even the fallback page renders A buy box rather than none.
    publishOffer(answered ? answers : null);

    if (!answered) {
      each('[data-atq-personalised]', document, drop);
      showFallback(E, answers);
      return;
    }

    /*
      `|| 1` is the prototype's own `computeTier(a)||1`. A run that reached the results
      without Q3 or Q8 still gets a full, personalised page on tier 1 — the types come
      from Q4/Q5/Q7 and have their own safety nets, so there is always something to
      show. Bailing to the fallback here instead, as this used to, both hid a diagnosis
      it could have rendered and handed the offer to the merchant setting, which is the
      most expensive page rather than the prototype's cheapest.
    */
    var tier = E.computeTier(answers) || 1;

    document.documentElement.setAttribute(ROOT_TIER, String(tier));
    var formula = resolveFormula(E, answers);
    document.documentElement.setAttribute(ROOT_FORMULA, formula);
    publishGender(formula);

    bindCart(answers, E);

    var shopperName = nameFrom(state);
    each('[data-atq-section]', document, function (section) {
      switch (section.getAttribute('data-atq-section')) {
        case 'diagnosis':
          fillDiagnosis(section, answers, shopperName, E);
          fillRootCause(section, answers, E);
          break;
        case 'ritual':
          fillRitual(section, answers, E);
          break;
        case 'addon':
          fillAddon(section, answers, E);
          break;
        case 'inside':
          fillInside(section, answers, E);
          break;
      }
    });
  }

  function start() {
    /*
      Registered unconditionally, and never `once`: a shopper can reopen the quiz
      with ?quiz=1 and finish it again without the page ever reloading, and the
      results behind it have to follow the new answers.

      The listener goes on before the `quizWillRun` test on purpose. `_complete()`
      can fire synchronously while <norse-quiz> upgrades — a shopper coming back
      from the email signup redirect with the analysing screen already spent never
      sees the overlay at all — and whether that happens before or after this file
      runs depends on section order in the template. Either order is safe: the event
      persists `completed: true` BEFORE it dispatches, so a complete we missed is a
      complete `quizWillRun` sees.
    */
    /*
      A finished run is the shopper stating their answers, so any `?offer=` / `?formula=`
      left sticky in the session stands down before the page is filled — see
      `clearSticky` in sections/norse-acne-results.liquid for what that cost QA.
    */
    document.addEventListener('norse:quiz-complete', function () {
      if (window.AtqOffer && typeof window.AtqOffer.clearSticky === 'function') {
        window.AtqOffer.clearSticky();
      }
      apply();
    });

    // Outside `apply()` on purpose: the buy box and the carousel are both there even
    // when no answers resolved, so the stars have to work on the fallback page too.
    bindReviewScroll();

    if (!quizWillRun()) apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
